import { useState, useEffect, useRef, ReactNode } from 'react'
import { Quote } from '@cinch-labs/design-system'
import { isSSR } from '@cinch-labs/shared-util'
import { StickyNav } from '../sticky-nav'
import { StoryBlokCarReviewSection } from '../../types/car-review-page'
import { renderWithResolver } from '@cinch-nx/landing-ui-shared'
import sharedStyles from '../../car-reviews-v2.module.css'
import cx from 'classnames'
import styles from './review-section.module.css'
import { ChevronBackToTopIcon } from '@cinch-nx/design-system-icons'
import { Media } from '../media'
import { StoryblokImage } from '@cinch-nx/data-storyblok'

export interface ReviewSectionsProps {
  make: string
  model: string
  children: ReactNode
  reviewSections: StoryBlokCarReviewSection[]
  imageCount: number
  placeholderImage: StoryblokImage
  areVehiclesInStock?: boolean
}
const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  })
}

export const ReviewSections = ({
  make,
  model,
  reviewSections,
  children,
  imageCount,
  placeholderImage,
  areVehiclesInStock,
}: ReviewSectionsProps) => {
  const reviewSectionsRef = useRef<HTMLDivElement>(null)
  const [windowTopScrollPosition, setWindowTopScrollPosition] = useState(0)
  const [windowBottomScrollPosition, setWindowBottomScrollPosition] =
    useState(0)

  const isClient = !isSSR()

  const handleScroll = () => {
    if (isClient) {
      const windowTopPosition = window.scrollY
      const windowBottomPosition = window.scrollY + window.innerHeight

      setWindowTopScrollPosition(windowTopPosition)
      setWindowBottomScrollPosition(windowBottomPosition)
    }
  }

  useEffect(() => {
    if (isClient) {
      window.addEventListener('scroll', handleScroll, { passive: true })

      return () => {
        window.removeEventListener('scroll', handleScroll)
      }
    }

    return () => null
  }, [handleScroll])

  let offsetPosition = 0

  if (reviewSectionsRef.current) {
    const reviewSections = reviewSectionsRef.current.offsetTop
    const negativeOffset = 54

    offsetPosition = reviewSections - negativeOffset
  }

  const showStickyNav = windowTopScrollPosition > offsetPosition

  return (
    <div ref={reviewSectionsRef}>
      {isClient && showStickyNav && (
        <StickyNav
          make={make}
          model={model}
          windowBottomScrollPosition={windowBottomScrollPosition}
          areVehiclesInStock={areVehiclesInStock}
        />
      )}

      <div className={cx(sharedStyles.inner, sharedStyles.sectionWrapper)}>
        {reviewSections.map((section) => (
          <section
            className={styles.carSectionWrapper}
            id={section.title.toLocaleLowerCase().replace(' ', '-')}
            key={section.title.toLocaleLowerCase().replace(' ', '-')}
          >
            <h2 className={styles.title}>{section.title}</h2>
            <p className={styles.sectionsRating}>
              Our rating: {section.rating}/10
            </p>
            {section.image ? (
              <Media
                imageCount={imageCount}
                imageSrc={section.image.filename ?? placeholderImage.filename}
                title={section.title}
                alt={section.image.alt ?? ''}
              />
            ) : null}
            <div className={styles.body}>
              {renderWithResolver(section.body)}
              {section.quote ? <Quote text={section.quote} /> : null}
            </div>
          </section>
        ))}

        <div className={styles.chevronBackToTop} onClick={scrollToTop}>
          <ChevronBackToTopIcon />

          <button className={styles.buttonBackToTop}>Back to top</button>
        </div>
      </div>
      {children}
    </div>
  )
}
